import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CustomErrorStateMatcher } from '../../interfaces/custom-error-state-matcher';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DataSourceOptionsService } from '../../services/data-source-options/data-source-options.service';
import { FormField } from '../../interfaces/form-field.interface';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { ReferenceField } from '../../interfaces/reference-field.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Utils } from '../../utils/utils.class';
import { ValidatorUtils } from '../../utils/validator-utils';
import { FieldCondtitionUtils } from '../../utils/field-condition-utils';
import { ConfigServiceProvider } from '../../services/config/config.service';

@Component({
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
  selector: 'sellions-forms-renderer',
  templateUrl: './renderer.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class SellionsFormsRendererComponent implements OnInit, OnDestroy {
  @Input() display: 'block' | 'inline' | 'inline-block' | 'flex' | 'none' = 'block';
  @Input() answers: any;
  @Input() formData: { [key: string]: any };
  @Input() disabled: boolean;
  @Input() floatLabel: 'auto' | 'always' = 'auto';
  @Input() displayListButtonIcons = false;

  @Input() set dataViewBaseAPI(value: string) {
    this.setDataViewBaseAPI(value);
  }

  @Input() set source(value: FormField[]) {
    this.setFormFields(value);
  }

  @Output() formChange: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Output() fieldsManagementFinished: EventEmitter<true> = new EventEmitter<true>();
  @Output() referencesChange: EventEmitter<{
    [controlId: string]: ReferenceField[];
  }> = new EventEmitter<{ [controlId: string]: ReferenceField[] }>();

  public form: FormGroup;
  public fields: FormField[];
  public formValid = true;
  public matcher = new CustomErrorStateMatcher(true);
  public references: { [controlId: string]: ReferenceField[] } = {};
  private unsubscribe$ = new Subject<void>();
  private engineApiUrl: string;
  private valueInput: FormField[];
  private _fieldsManagementFinished: boolean = true;

  constructor(private dictionaryService: DataSourceOptionsService, private formBuilder: FormBuilder, private configService: ConfigServiceProvider) {
    this.form = formBuilder.group({});
  }

  ngOnInit() {
    this.configService.setBackendBaseAPI(this.engineApiUrl);

    if (this.formData) {
      this.patchForm(this.formData);
    }
  }

  private patchForm(data: { [key: string]: any }): void {
    this.fields.forEach((field) => {
      const formField = this.form.get(field.id);
      if (formField == null) {
        return;
      }

      if (field.type === 'list') {
        this.handleListField(formField as FormArray, field, data[field.id]);
      } else {
        formField.patchValue(data[field.id]);
      }
    });
  }

  private handleListField(formArray: FormArray, field: FormField, fieldData: any): void {
    if (field.multiForm) {
      this.handleSubformPatch(formArray, field.id, fieldData);
    } else {
      formArray.at(0).patchValue(fieldData);
    }
  }

  private handleSubformPatch(formArray: FormArray, fieldId: string, fieldData: any): void {
    if (this.isFieldDataNotArray(fieldData)) {
      return;
    }

    const subformData = fieldData as any[];

    for (let i = 0; i < subformData.length; ++i) {
      if (i > 0) {
        this.addNewList(fieldId);
      }
      formArray.at(i).patchValue(subformData[i]);
    }
  }

  private isFieldDataNotArray(fieldData: any): boolean {
    if (fieldData == null) {
      return true;
    }

    if (!Array.isArray(fieldData)) {
      console.warn('[SellionsFormsRendererComponent] A list field is occured, but the provided data is not a array', fieldData);
      return true;
    }

    return false;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private setDataViewBaseAPI(api: string): void {
    if (api[api.length - 1] === '/') {
      this.engineApiUrl = api.slice(0, -1);
    } else {
      this.engineApiUrl = api;
    }
  }

  private setFormFields(formFields: FormField[]): void {
    if (formFields && (!this.valueInput || JSON.stringify(formFields) !== JSON.stringify(this.valueInput))) {
      this.valueInput = Utils.copyArray(formFields);
      this.fields = Utils.copyArray(formFields);
      this.populateForm(this.fields);
    }
  }

  private populateForm(fields: FormField[]): void {
    this.getFieldsOptions(fields);

    this.manageFields();

    if (this.answers) {
      this.fillFormAnswers();
    }

    this.formChange.emit(this.form);

    this.manageFieldsOnFormChange();
  }

  private getFieldsOptions(fields: FormField[]): void {
    if (this.engineApiUrl) {
      this.dictionaryService.downloadFieldsOptions(fields, this.engineApiUrl);
    } else {
      console.warn('[SellionsFormsRendererComponent] The Engine API URL is missing. Unable to fetch field options');
    }
  }

  private fillFormAnswers(): void {
    Object.keys(this.answers).forEach((key) => {
      const control = this.form.controls[key];
      if (control) {
        const answer = this.answers[key];
        if (typeof answer === 'object' && answer.length >= 1) {
          for (let i = 0; i < answer.length; i++) {
            this.addNewList(key);
          }
        }
        control.patchValue(this.answers[key]);
      }
    });
  }

  private manageFieldsOnFormChange(): void {
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this._fieldsManagementFinished) {
        this.manageFields();
        this.formChange.emit(this.form);
      }
    });
  }

  private manageFields(): void {
    if (!this.fields) {
      return;
    }

    this._fieldsManagementFinished = false;

    this.fields.forEach((control) => {
      this.manageSingleField(control, this.form);

      if (control.type === 'list' && this.form.controls[control.id]) {
        this.manageListField(control);
      }
    });

    this._fieldsManagementFinished = true;
    this.fieldsManagementFinished.emit(true);
  }

  private manageListField(listControl: FormField): void {
    const listOfGroup = this.form.controls[listControl.id] as FormArray;
    const definitions = listControl.subForm.definition;

    if(listControl.required || listControl.requiredIfShown) {
      const answer = this.answers ? this.answers[listControl.id] : null;
      if (!listOfGroup.length && (!answer || answer.length == 0)) {
        let numberOfPredefinedOptions = listControl.predefinedValue ? listControl.predefinedValue.length : 0;
        do {
          listOfGroup.push(this.formBuilder.group([]));
        } while(listOfGroup.length < numberOfPredefinedOptions);
      }
      for (let i = 0; i < listOfGroup.controls.length; ++i) {
        const formGroup = listOfGroup.controls[i] as FormGroup;
        definitions.forEach((def) => {
          this.manageSingleField(def, formGroup, i, listControl);
        });
      }
    }
  }

  private manageSingleField(control: FormField, formGroup: FormGroup, index: number = 0, subForm: FormField = null): void {
    const conditions = control.conditions;
    const conditionConjuction = control.conditionsConjuction;
    const formControls = formGroup.controls;
    const fields = subForm ? subForm.subForm.definition : this.fields;

    if (FieldCondtitionUtils.areConditionsMet(conditions, conditionConjuction, formControls, fields) && !formControls[control.id]) {
      this.addControl(control, formGroup);
    }

    if (!FieldCondtitionUtils.areConditionsMet(conditions, conditionConjuction, formControls, fields) && formControls[control.id]) {
      formGroup.removeControl(control.id);
    }

    //TODO do zaorania po zakończeniu prac nad LO, mergu zmian z sellions/development i ew. migracji starych formsów
    if (formControls[control.id] && !formControls[control.id].value) {
      this.fillFormInputWithPredefinedValue(control, formControls, index, subForm);
    }
  }

  private addControl(control: FormField, formGroup: FormGroup): void {
    if (control.type === 'list') {
      const formArray = this.formBuilder.array([]);
      formGroup.addControl(control.id, formArray);
    } else {
      const formControl = this.createFormControl(control);

      if (control.type === 'bool') {
        formControl.setValue(false);
      }

      if (control.type === 'checkbox') {
        if (!control.predefinedValue) {
          formControl.setValue([]);
        }
      }

      formGroup.addControl(control.id, formControl);
    }
  }

  private createFormControl(control: FormField): FormControl {
    return this.formBuilder.control(
      {
        value: control.predefinedValue || null,
        disabled: control.disabled,
      },
      ValidatorUtils.generateValidators(control),
    );
  }

  private fillFormInputWithPredefinedValue(
    control: FormField,
    formControls: { [key: string]: AbstractControl },
    index: number = 0,
    subForm: FormField = null,
  ): void {
    if (control.predefinedValue && !formControls[control.id].touched) {
      formControls[control.id].patchValue(this.getPredefinedValue(control));
    }
    if (subForm && Array.isArray(subForm.predefinedValue) && subForm.predefinedValue.length > index) {
      let formControl = formControls[control.id];
      if(!formControl.touched) {
        formControl.patchValue(this.getPredefinedListValue(control, subForm.predefinedValue[index][control.id]));
      }
    }
  }

  private getPredefinedValue(control: FormField): any {
    if (control.type === 'reference') {
      return Utils.evaluateDisplayStrings(control.predefinedValue, control);
    }
    if (control.dataSource === 'dataView') {
      return this.getPredifinedDataViewValue(control.id, control.predefinedValue);
    }
    return control.predefinedValue;
  }

  private getPredefinedListValue(control: FormField, predefinedValue: any): any {
    if (control.type === 'reference') {
      return Utils.evaluateDisplayStrings(predefinedValue, control);
    }
    if (control.dataSource === 'dataView') {
      return this.getPredifinedDataViewValue(control.id, predefinedValue);
    }
    return predefinedValue;
  }

  private getPredifinedDataViewValue(controlId: string, predefinedValueId: string): ReferenceField | string {
    if (this.references[controlId] && this.references[controlId].length > 0) {
      return this.references[controlId].find((el) => el.id === predefinedValueId);
    }
    return predefinedValueId;
  }

  public getControlFormArray(controlId: string): AbstractControl[] {
    if ((this.form.controls[controlId] as FormArray) !== undefined) {
      return (this.form.controls[controlId] as FormArray).controls;
    }
    return new Array<AbstractControl>();
  }

  public addNewList(name: string): void {
    const list = this.fields.find((control) => control.id === name);
    const formGroup = this.createSubForm(list);
    (this.form.controls[name] as FormArray).push(formGroup);
  }

  private createSubForm(control: FormField): FormGroup {
    const subForm = this.formBuilder.group([]);
    for (const def of control.subForm.definition) {
      this.manageSingleField(def, subForm);
    }

    return subForm;
  }

  public removeList(control: FormField, index: number): void {
    if(control.predefinedValue && Array.isArray(control.predefinedValue) && control.predefinedValue.length > index) {
      control.predefinedValue.splice(index);
    }
    (this.form.controls[control.id] as FormArray).removeAt(index);
  }

  public getControlDisplay(control: FormField): string {
    return control.hidden ? 'none' : this.display;
  }

  public hasChanges(): boolean {
    return this.form.dirty && !this.form.pristine;
  }

  public isFormValid(): boolean {
    this.markAsTouched(this.form);
    this.form.updateValueAndValidity();
    this.formValid = this.form.valid;
    this.matcher.setFormValid(this.formValid);
    return this.formValid;
  }

  // Workaround: somehow the markAsTouched method does not affect inner controls...
  private markAsTouched(providedFormCollection: FormGroup | FormArray): void {
    providedFormCollection.markAsTouched();
    for (const controlId in providedFormCollection.controls) {
      const control: AbstractControl = providedFormCollection.controls[controlId];
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAsTouched(control);
      }
      if (control.enabled) {
        control.markAsTouched();
      }
    }
  }

  isFieldInvalid(id: string): boolean {
    return (
      (this.form.controls[id] && this.form.controls[id].touched && this.form.controls[id].invalid) ||
      (!this.formValid && this.form.controls[id] && this.form.controls[id].invalid)
    );
  }

  public isValidInnerField(controlId: string, innerControlId: string): boolean {
    if (!this.form.controls[controlId]) {
      return false;
    }
    const innerForm = this.form.controls[controlId] as FormGroup;
    return (
      (innerForm[innerControlId] && innerForm[innerControlId].touched && innerForm[innerControlId].invalid) ||
      (!this.formValid && innerForm[innerControlId].invalid)
    );
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_forms/projects/forms/src/lib/components/renderer/renderer.component.ts